import { useCallback, useEffect, useRef, useState } from 'react'
import { fromCoreState, startNewRun, type GameState } from '../game'
import { canonicalJson, type SaveDocument } from '../api'
import { clearLocalSave, loadStoredSaveRecord, persistLocalSave, saveLocalSave } from '../storage'
import type { Panel } from '../view-types'

export type LocalStateGuard = { version: number; key: string }
export type SaveStatus = 'saved' | 'memory'

type UseLocalGameOptions = {
  activeUserId: string | null
  authResolved: boolean
  ownerGeneration: number
  isCurrentOwner: (generation: number, userId: string | null) => boolean
}

function stateKey(state: GameState | null): string {
  return state ? canonicalJson(state.coreState) : ''
}

function hasLocalProgress(state: GameState | null): state is GameState {
  return Boolean(state?.runStarted || state?.history.length || state?.completedRuns || state?.endingsCollected.length || state?.fateCollected.length)
}

export function useLocalGame({ activeUserId, authResolved, ownerGeneration, isCurrentOwner }: UseLocalGameOptions) {
  const [save, setSave] = useState<GameState | null>(null)
  const [saveOwner, setSaveOwner] = useState<string | null>(null)
  const [projectionReady, setProjectionReady] = useState(false)
  const [localLoaded, setLocalLoaded] = useState(false)
  const [screen, setScreen] = useState<'home' | 'game'>('home')
  const [panel, setPanel] = useState<Panel>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved')
  const [guestSave, setGuestSave] = useState<GameState | null>(null)
  const [saveUpdatedAt, setSaveUpdatedAt] = useState<string | null>(null)
  const [guestSaveUpdatedAt, setGuestSaveUpdatedAt] = useState<string | null>(null)
  const [birthIntroToken, setBirthIntroToken] = useState<number | null>(null)
  const [restartMemories, setRestartMemories] = useState<GameState['history'] | null>(null)
  const latestSaveRef = useRef<GameState | null>(null)
  const guestSaveRef = useRef<GameState | null>(null)
  const localOperationVersionRef = useRef(0)
  const birthIntroTokenRef = useRef<number | null>(null)
  const birthIntroSequenceRef = useRef(0)

  const recordLocalOperation = useCallback((next: GameState | null): number => {
    localOperationVersionRef.current += 1
    latestSaveRef.current = next
    return localOperationVersionRef.current
  }, [])

  const currentLocalGuard = useCallback((): LocalStateGuard => ({
    version: localOperationVersionRef.current,
    key: stateKey(latestSaveRef.current)
  }), [])

  const isCurrentLocalGuard = useCallback((generation: number, userId: string | null, guard: LocalStateGuard): boolean => (
    isCurrentOwner(generation, userId)
      && guard.version === localOperationVersionRef.current
      && guard.key === stateKey(latestSaveRef.current)
  ), [isCurrentOwner])

  const getLatestLocal = useCallback(() => latestSaveRef.current, [])

  useEffect(() => {
    if (!authResolved) return
    const generation = ownerGeneration
    const owner = activeUserId
    localOperationVersionRef.current += 1
    latestSaveRef.current = null
    setSave(null)
    setSaveUpdatedAt(null)
    setSaveOwner(null)
    setLocalLoaded(false)
    setProjectionReady(false)
    setScreen('home')
    birthIntroTokenRef.current = null
    setBirthIntroToken(null)

    const load = async () => {
      const [localRecord, guestRecord] = await Promise.all([loadStoredSaveRecord(owner), owner ? loadStoredSaveRecord(null) : Promise.resolve(null)])
      if (!isCurrentOwner(generation, owner)) return
      const local = localRecord?.state || null
      const guest = guestRecord?.state || null
      guestSaveRef.current = owner ? guest : local
      setGuestSave(owner ? guest : local)
      setGuestSaveUpdatedAt(owner ? guestRecord?.savedAt || null : localRecord?.savedAt || null)
      if (local) {
        recordLocalOperation(local)
        setSave(local)
        setSaveUpdatedAt(localRecord?.savedAt || null)
      }
      setSaveOwner(owner)
      setLocalLoaded(true)
      // Account projection waits for cloud reconciliation. A guest has no
      // remote authority and can become interactive as soon as local storage is read.
      setProjectionReady(!owner)
      if (local) setScreen(local.runStarted ? 'game' : 'home')
    }
    void load()
    return () => {
      if (generation === ownerGeneration) localOperationVersionRef.current += 1
    }
  }, [activeUserId, authResolved, isCurrentOwner, ownerGeneration, recordLocalOperation])

  useEffect(() => {
    latestSaveRef.current = authResolved && saveOwner === activeUserId ? save : null
  }, [activeUserId, authResolved, save, saveOwner])

  useEffect(() => {
    if (!authResolved || !projectionReady || !save || saveOwner !== activeUserId || !isCurrentOwner(ownerGeneration, activeUserId)) return
    const savedAt = saveUpdatedAt || new Date().toISOString()
    setSaveStatus(saveLocalSave(save, activeUserId, savedAt) === 'localStorage' ? 'saved' : 'memory')
    if (!activeUserId) {
      guestSaveRef.current = save
      setGuestSave(save)
    }
  }, [activeUserId, authResolved, isCurrentOwner, ownerGeneration, projectionReady, save, saveOwner, saveUpdatedAt])

  const commit = useCallback((next: GameState) => {
    if (!authResolved || !projectionReady || saveOwner !== activeUserId) return
    recordLocalOperation(next)
    const savedAt = new Date().toISOString()
    setSaveUpdatedAt(savedAt)
    if (!activeUserId) {
      guestSaveRef.current = next
      setGuestSave(next)
      setGuestSaveUpdatedAt(savedAt)
    }
    setSave(next)
  }, [activeUserId, authResolved, projectionReady, recordLocalOperation, saveOwner])

  const start = useCallback(() => {
    if (!authResolved || !projectionReady || saveOwner !== activeUserId || birthIntroTokenRef.current !== null) return
    setRestartMemories(save?.runStarted ? save.history.slice(-4) : null)
    const next = startNewRun(save)
    const savedAt = new Date().toISOString()
    const token = ++birthIntroSequenceRef.current
    birthIntroTokenRef.current = token
    setBirthIntroToken(token)
    recordLocalOperation(next)
    setSaveUpdatedAt(savedAt)
    if (!activeUserId) {
      guestSaveRef.current = next
      setGuestSave(next)
      setGuestSaveUpdatedAt(savedAt)
    }
    setSave(next)
    setSaveOwner(activeUserId)
    setScreen('game')
    setPanel(null)
  }, [activeUserId, authResolved, projectionReady, recordLocalOperation, save, saveOwner])

  const clearBirthIntro = useCallback(() => {
    if (birthIntroTokenRef.current === null) return
    birthIntroTokenRef.current = null
    setBirthIntroToken(null)
  }, [])

  const continueRun = useCallback(() => {
    if (save && projectionReady && saveOwner === activeUserId) {
      setScreen('game')
      setPanel(null)
    }
  }, [activeUserId, projectionReady, save, saveOwner])

  const resetLocal = useCallback(async () => {
    const owner = activeUserId
    const generation = ownerGeneration
    if (!authResolved || !projectionReady || saveOwner !== owner || !isCurrentOwner(generation, owner)) return
    const localGuard = currentLocalGuard()
    const label = owner ? '账号' : '游客'
    if (!window.confirm(`确定清除这台设备上的${label}存档吗？此操作会同时清除 localStorage 和 IndexedDB，不会删除云端存档。`)) return
    await clearLocalSave(owner)
    if (!isCurrentLocalGuard(generation, owner, localGuard)) return
    recordLocalOperation(null)
    clearBirthIntro()
    setSave(null)
    setSaveUpdatedAt(null)
    setSaveOwner(owner)
    if (!owner) {
      guestSaveRef.current = null
      setGuestSave(null)
      setGuestSaveUpdatedAt(null)
    }
    setProjectionReady(true)
    setScreen('home')
    setPanel(null)
  }, [activeUserId, authResolved, clearBirthIntro, currentLocalGuard, isCurrentLocalGuard, isCurrentOwner, ownerGeneration, projectionReady, recordLocalOperation, saveOwner])

  const applyCloudDocument = useCallback((document: SaveDocument, generation: number, owner: string, expectedGuard?: LocalStateGuard): boolean => {
    if (!isCurrentOwner(generation, owner)) return false
    if (expectedGuard && !isCurrentLocalGuard(generation, owner, expectedGuard)) return false
    const restored = fromCoreState(document.state, document.revision)
    recordLocalOperation(restored)
    saveLocalSave(restored, owner, document.updatedAt)
    setSave(restored)
    setSaveUpdatedAt(document.updatedAt)
    setSaveOwner(owner)
    setScreen(restored.runStarted ? 'game' : 'home')
    return true
  }, [isCurrentOwner, recordLocalOperation])

  const projectRevision = useCallback(async (candidate: GameState, localVersion: number, revision: number, updatedAt: string, owner: string, generation: number): Promise<boolean> => {
    const guard = { version: localVersion, key: stateKey(candidate) }
    const latest = latestSaveRef.current
    if (!latest || !isCurrentLocalGuard(generation, owner, guard)) return false
    const projected = { ...latest, saveRevision: revision }
    const location = await persistLocalSave(projected, owner, updatedAt)
    if (!isCurrentOwner(generation, owner)) return false
    const current = latestSaveRef.current
    if (current && isCurrentLocalGuard(generation, owner, guard)) {
      const currentProjection = { ...current, saveRevision: revision }
      latestSaveRef.current = currentProjection
      setSave(currentProjection)
      setSaveUpdatedAt(updatedAt)
      setSaveStatus(location === 'localStorage' ? 'saved' : 'memory')
    } else if (current) {
      saveLocalSave(current, owner, saveUpdatedAt || new Date().toISOString())
    }
    return true
  }, [isCurrentLocalGuard, isCurrentOwner, saveUpdatedAt])

  const discardGuestSave = useCallback(async () => {
    await clearLocalSave(null)
    guestSaveRef.current = null
    setGuestSave(null)
    setGuestSaveUpdatedAt(null)
  }, [])

  return {
    save,
    saveOwner,
    projectionReady,
    setProjectionReady,
    localLoaded,
    screen,
    setScreen,
    panel,
    setPanel,
    saveStatus,
    saveUpdatedAt,
    birthIntroToken,
    restartMemories,
    clearBirthIntro,
    guestSave,
    guestSaveUpdatedAt,
    discardGuestSave,
    commit,
    start,
    continueRun,
    resetLocal,
    recordLocalOperation,
    currentLocalGuard,
    isCurrentLocalGuard,
    getLatestLocal,
    applyCloudDocument,
    projectRevision,
    hasLocalProgress
  }
}
